import React, { useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  formatNumericCurrency,
  queryIsLoading,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprint,
  useTranslation,
} from "utils";

import { CarbonProgram } from "models";

import { Checkbox, Flex, Form, Icon, OutlinedCard, SkeletonWrapper, Typography } from "components";

export const SubmitForVerificationCard = ({
  farmId,
  carbonProgram,
  year,
  formSubmitted = false,
  onAllCheckboxesChecked,
}: {
  farmId?: string;
  carbonProgram?: CarbonProgram;
  year: number;
  formSubmitted: boolean;
  onAllCheckboxesChecked: (allChecked: boolean) => void;
}) => {
  const { t } = useTranslation("footprinting");
  const [payoutCheckbox, setPayoutCheckbox] = useState(false);
  const [termsCheckbox, setTermsCheckbox] = useState(false);

  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], carbonProgram?.farmLevelFootprinting?.programCrops);
  const dairyProgressQueries = useGetFootprintingDairyFootprints(farmId, [year]);

  const { totalPayout } = calculateFootprintingProgress(farmProgressQuery.data, [
    ...cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data),
    ...dairyProgressQueries.filter((dpq) => !!dpq.data).map((dpq) => dpq.data),
  ]);

  const loading = [farmProgressQuery, ...cropProgressQueries, ...dairyProgressQueries].some(queryIsLoading);
  const allChecked = (carbonProgram?.platformFeatures.footprinting?.payouts === false ? true : payoutCheckbox === true) && termsCheckbox === true;

  useEffect(() => {
    onAllCheckboxesChecked(allChecked);
  }, [allChecked, onAllCheckboxesChecked]);

  return (
    <Flex vertical>
      <Flex align="center" className="padding-150 padding-left-100">
        <SkeletonWrapper className="margin-0 skeleton-rectangular" width="50%" height="2rem" type="block" isLoading={loading}>
          <Icon size="l" iconName="klim/page-checkmark" />
          <Typography variant="h3" className="margin-left-100">
            {t("submitForVerificationCard.title")}
          </Typography>
        </SkeletonWrapper>
      </Flex>

      <OutlinedCard dataTestId="submit-verification-card">
        <Flex vertical gap={24}>
          <SkeletonWrapper className="margin-0 skeleton-rectangular" width="40%" height="1.5rem" type="block" isLoading={loading}>
            <Typography variant="h4">{t("submitForVerificationCard.subTitle")}</Typography>
          </SkeletonWrapper>
          <SkeletonWrapper className="margin-0 skeleton-rectangular" type="paragraph" isLoading={loading}>
            <Typography variant="paragraph-m" color="grey-600">
              {t("submitForVerificationCard.description")}
            </Typography>
            {carbonProgram?.platformFeatures.footprinting?.payouts === false ? null : (
              <Typography variant="paragraph-m" color="grey-600">
                {t("submitForVerificationCard.descriptionPayout")}
              </Typography>
            )}
          </SkeletonWrapper>
        </Flex>

        <Flex vertical className="margin-top-100">
          <hr className="margin-0" />
          {carbonProgram?.platformFeatures.footprinting?.payouts === false ? null : (
            <Flex align="center" justify="space-between" className="margin-top-100">
              <SkeletonWrapper className="skeleton-rectangular" height="1rem" width="50%" type="block" isLoading={loading}>
                <Typography variant="paragraph-m" bold>
                  {t("submitForVerificationCard.total", { year })}
                </Typography>
              </SkeletonWrapper>
              <SkeletonWrapper className="skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
                <div className="footprinting-payout-card-amount">
                  <Icon iconName="tabler/clock" />
                  {formatNumericCurrency(totalPayout, { currency: carbonProgram?.currency })}
                </div>
              </SkeletonWrapper>
            </Flex>
          )}

          <Flex className="margin-top-100" vertical gap={16}>
            {carbonProgram?.platformFeatures.footprinting?.payouts === false ? null : (
              <Form.Item
                validateStatus={!payoutCheckbox && formSubmitted ? "error" : ""}
                help={!payoutCheckbox && formSubmitted ? t("submitForVerificationCard.tAndCErrorMessage") : ""}
                className={!payoutCheckbox && formSubmitted ? "checkbox-error" : ""}
              >
                <SkeletonWrapper className="margin-0 skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
                  <Checkbox checked={payoutCheckbox} onChange={() => setPayoutCheckbox(!payoutCheckbox)} />
                  <Typography variant="paragraph-m">{t("submitForVerificationCard.tAndCPayout", { year })}*</Typography>
                </SkeletonWrapper>
              </Form.Item>
            )}

            <Form.Item
              validateStatus={!termsCheckbox && formSubmitted ? "error" : ""}
              help={!termsCheckbox && formSubmitted ? t("submitForVerificationCard.tAndCErrorMessage") : ""}
              className={!termsCheckbox && formSubmitted ? "checkbox-error" : ""}
            >
              <SkeletonWrapper className="margin-0 skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
                <Checkbox checked={termsCheckbox} onChange={() => setTermsCheckbox(!termsCheckbox)} />
                <Typography variant="paragraph-m">{t("submitForVerificationCard.tAndCEdit", { year })}*</Typography>
              </SkeletonWrapper>
            </Form.Item>
          </Flex>
        </Flex>
      </OutlinedCard>
    </Flex>
  );
};
